import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import {
  DS_ATTENDANCE_AUDIT_TYPE_LABEL,
  DSAttendanceAuditType
} from '@/constants/ds-attendance.constants'
import type {
  ActivityRequestTimelineEvent,
  ActivityRequestTimelineResponse
} from '@/types/activity-requests.types'

import { formatDateTimeInStation } from './activity-request-format'

export interface TimelineEntry {
  key: string
  at: string
  atLabel: string
  title: string
  subtitle: string | null
  tone: QueueTone
}

const TITLES: Record<string, (id: number) => string> = {
  REQUEST_CREATED: (id) => `Solicitação #${id} criada`,
  CONTAINER_SUBMIT: (id) => `Registro #${id} enviado`,
  CONTAINER_REJECT: (id) => `Registro #${id} reprovado`,
  CONTAINER_APPROVE: (id) => `Registro #${id} aprovado`,
  REQUEST_REOPENED: (id) => `Solicitação #${id} reaberta`,
  REQUEST_RESCHEDULED: (id) => `Prazo da solicitação #${id} recalculado`,
  REQUEST_CANCELLED: (id) => `Solicitação #${id} cancelada`,
  REQUEST_FULFILLED: (id) => `Solicitação #${id} encerrada pelo envio`,
  APPROVAL_REVERTED: (id) => `Decisão do registro #${id} revertida`
}

const TONES: Record<string, QueueTone> = {
  REQUEST_CREATED: 'neutral',
  CONTAINER_SUBMIT: 'info',
  CONTAINER_REJECT: 'crit',
  CONTAINER_APPROVE: 'ok',
  REQUEST_REOPENED: 'warn',
  REQUEST_RESCHEDULED: 'warn',
  REQUEST_CANCELLED: 'crit',
  REQUEST_FULFILLED: 'ok',
  APPROVAL_REVERTED: 'warn'
}

/**
 * M2-C (§6.4) — criada, enviada, reprovada com a justificativa, reaberta, reagendada e
 * cancelada com o motivo. Tipo desconhecido aparece com o rótulo do catálogo ou cru:
 * sumir com um evento esconderia justamente o que alguém veio investigar.
 */
/** Instante para ordenar; audit sem instante vai para o fim. */
const sortKey = (at: string | null): number => {
  const time = at ? Date.parse(at) : Number.NaN
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
}

/** APPROVAL_REVERTED serve a aprovação e a reprovação; `values.status` diferencia as duas. */
function revertedTitle(event: ActivityRequestTimelineEvent): string | null {
  if (event.actionType !== 'APPROVAL_REVERTED') return null
  const status = event.values?.status
  if (status === 'PENDING_AUDIT') return `Reprovação do registro #${event.containerId} revertida`
  if (status === 'AUDIT_IN_PROGRESS') return `Aprovação do registro #${event.containerId} revertida`
  return null
}

export function mapActivityRequestTimeline(
  response: ActivityRequestTimelineResponse,
  offset: string
): TimelineEntry[] {
  return [...response.events]
    .map((event, index) => ({ event, index }))
    .sort((a, b) => sortKey(a.event.at) - sortKey(b.event.at) || a.index - b.index)
    .map(({ event }) => {
      const title =
        revertedTitle(event) ??
        TITLES[event.actionType]?.(event.containerId) ??
        `${DS_ATTENDANCE_AUDIT_TYPE_LABEL[event.actionType as DSAttendanceAuditType] ?? event.actionType} (#${event.containerId})`
      const parts = [
        event.actorName,
        event.justification ? `Motivo: “${event.justification}”` : null
      ].filter((part): part is string => !!part)
      return {
        key: String(event.auditId),
        at: event.at ?? '',
        atLabel:
          (event.at && formatDateTimeInStation(event.at, offset)) || 'horário não registrado',
        title,
        subtitle: parts.length ? parts.join(' · ') : null,
        tone: TONES[event.actionType] ?? 'neutral'
      }
    })
}

export interface StripPoint {
  key: string
  label: string
  at: string
  pct: number
}

const STRIP_LABELS: Record<string, string> = {
  expectedStart: 'Entrada prevista',
  expectedEnd: 'Saída prevista',
  effectiveIn: 'Entrada efetiva',
  effectiveOut: 'Saída efetiva',
  openedAt: 'Solicitação aberta',
  deadline: 'Prazo de envio',
  now: 'Agora'
}

/** Faixa "previsto × efetivo × prazo" (§6.4): pontos ordenados e posição relativa em %. */
export function buildDeadlineStrip(input: {
  expectedStart: string | null
  expectedEnd: string | null
  effectiveIn: string | null
  effectiveOut: string | null
  openedAt: string | null
  deadline: string | null
  nowMs: number | null
}): StripPoint[] {
  const raw: Array<[string, string | null]> = [
    ['expectedStart', input.expectedStart],
    ['expectedEnd', input.expectedEnd],
    ['effectiveIn', input.effectiveIn],
    ['effectiveOut', input.effectiveOut],
    ['openedAt', input.openedAt],
    ['deadline', input.deadline],
    ['now', input.nowMs === null ? null : new Date(input.nowMs).toISOString()]
  ]
  const points = raw
    .filter((entry): entry is [string, string] => !!entry[1] && !Number.isNaN(Date.parse(entry[1])))
    .map(([key, at]) => ({ key, at, time: Date.parse(at) }))
    .sort((a, b) => a.time - b.time)
  if (!points.length) return []
  const min = points[0].time
  const span = points[points.length - 1].time - min
  return points.map(({ key, at, time }) => ({
    key,
    label: STRIP_LABELS[key],
    at,
    pct: span > 0 ? Math.round(((time - min) / span) * 1000) / 10 : 0
  }))
}
